import { useMemo } from 'react';
import { toMoney } from 'components/Money';
import { calculateOtherFeePrice, getOtherFeeContributingLines, getOtherFeeRestrictionParams, getOtherFeeRestrictionQuantity, getOtherFeeRestrictionSubTotal, toFiniteNumber } from 'helpers/otherFeesCalculation';
/**
 * @function collectGroupTitles
 * @since 1.0.0
 * @hidden
 */
function collectGroupTitles(deal) {
    var _a;
    const titles = new Map();
    const addGroups = (groups) => {
        groups === null || groups === void 0 ? void 0 : groups.forEach((group) => {
            if ((group === null || group === void 0 ? void 0 : group.id) && group.title && !titles.has(group.id)) {
                titles.set(group.id, group.title);
            }
        });
    };
    (_a = deal.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
        var _a, _b, _c;
        addGroups((_a = item.item) === null || _a === void 0 ? void 0 : _a.groups);
        (_b = item.options) === null || _b === void 0 ? void 0 : _b.forEach((option) => { var _a; return addGroups((_a = option.item) === null || _a === void 0 ? void 0 : _a.groups); });
        (_c = item.packages) === null || _c === void 0 ? void 0 : _c.forEach((pack) => { var _a; return addGroups((_a = pack.package) === null || _a === void 0 ? void 0 : _a.groups); });
    });
    return titles;
}
/**
 * @function formatGroupList
 * @since 1.0.0
 * @hidden
 */
function formatGroupList(ids, groupTitles) {
    return ids
        .map((id) => groupTitles.get(id))
        .filter((title) => Boolean(title))
        .join(', ');
}
/**
 * @function formatRestrictionLabel
 * @since 1.0.0
 * @hidden
 */
function formatRestrictionLabel(restriction, restrictionValue, restrictionExclusion, groupTitles) {
    if (restriction !== 'SALE_ITEM_GROUP' || restrictionValue.length === 0) {
        return t('All items');
    }
    const included = formatGroupList(restrictionValue, groupTitles);
    const includedLabel = included
        ? `${t('Restricted to groups')}: ${included}`
        : t('Restricted to groups');
    const excluded = formatGroupList(restrictionExclusion !== null && restrictionExclusion !== void 0 ? restrictionExclusion : [], groupTitles);
    if (!excluded) {
        return includedLabel;
    }
    return `${includedLabel} · ${t('Excluded groups')}: ${excluded}`;
}
/**
 * @function formatFormula
 * @since 1.0.0
 * @hidden
 */
function formatFormula(deal, otherFee, restriction, restrictionValue, restrictionExclusion, usable) {
    const fee = otherFee.fee;
    if (!fee) {
        return '';
    }
    if (fee.type === 'FIXED') {
        return t('Fixed amount');
    }
    if (!usable || fee.value === null || fee.value === undefined) {
        return '';
    }
    if (fee.type === 'PERCENTAGE') {
        const subTotal = getOtherFeeRestrictionSubTotal(deal, restriction, restrictionValue, restrictionExclusion);
        return `${toMoney(subTotal)} × ${fee.value}%`;
    }
    if (fee.type === 'QUANTITY') {
        const quantity = getOtherFeeRestrictionQuantity(deal, restriction, restrictionValue, restrictionExclusion);
        return `${quantity} × ${toMoney(toFiniteNumber(fee.value))}`;
    }
    return '';
}
/**
 * @function useDealOtherFeesBreakdown
 * @since 1.0.0
 *
 * Builds a per-fee breakdown of the automatically calculated other fees
 * (catalog `Fee` still linked). Manual fees (`fee` detached) are omitted.
 *
 * This hook is free of any React context dependency so it can be used from
 * modal content rendered outside of the deal configurator context.
 *
 * @hidden
 */
export function useDealOtherFeesBreakdown(deal) {
    return useMemo(() => {
        var _a;
        if (!deal) {
            return { fees: [], total: 0 };
        }
        const groupTitles = collectGroupTitles(deal);
        const automaticFees = ((_a = deal.otherFees) !== null && _a !== void 0 ? _a : []).filter((otherFee) => otherFee.fee);
        const fees = automaticFees.map((otherFee, index) => {
            const fee = otherFee.fee;
            const params = getOtherFeeRestrictionParams(otherFee);
            const price = calculateOtherFeePrice(deal, otherFee);
            const rate = toFiniteNumber(fee.value);
            let lines = [];
            if (fee.type !== 'FIXED' && params.usable) {
                lines = getOtherFeeContributingLines(deal, params.restriction, params.restrictionValue, params.restrictionExclusion).map((line) => (Object.assign(Object.assign({}, line), { contribution: fee.type === 'PERCENTAGE'
                        ? line.amount * (rate / 100)
                        : line.quantity * rate })));
            }
            return {
                id: otherFee.id || `fee-${index}`,
                title: fee.title || otherFee.description,
                description: otherFee.description,
                type: fee.type,
                formula: formatFormula(deal, otherFee, params.restriction, params.restrictionValue, params.restrictionExclusion, params.usable),
                restrictionLabel: formatRestrictionLabel(params.restriction, params.restrictionValue, params.restrictionExclusion, groupTitles),
                lines,
                price
            };
        });
        return {
            fees,
            total: fees.reduce((sum, fee) => sum + fee.price, 0)
        };
    }, [deal]);
}
